import { create } from "zustand";
import { vendorService } from "../services";
import { IVendor, IPaginationMeta } from "../@types";
import { createVendor } from "../backend-services/vendor";

interface FetchOptions {
  startDate?: string;
  endDate?: string;
  actionType?: "initial" | "filter";
}

interface VendorStoreState {
  vendors: IVendor[];
  newVendors: IVendor[];
  isLoading: boolean;
  isLoadingInitial: boolean;
  isLoadingFilter: boolean;
  isLoadingMore: boolean;
  pagination: IPaginationMeta;
  error: boolean;
  range: string;
  searchText: string;
  fetchVendors: () => Promise<void>;
  fetchData: (options?: FetchOptions) => Promise<void> | undefined;
  fetchMoreData: () => Promise<void>;
  createVendor: (vendor: any) => Promise<void>;
  setSearchText: (text: string) => void;
}

export const useVendorStore = create<VendorStoreState>((set, get) => ({
  vendors: [],
  newVendors: [],
  isLoading: false,
  isLoadingInitial: true,
  isLoadingFilter: false,
  isLoadingMore: false,
  error: false,
  range: "",
  searchText: "",
  pagination: {
    cursor: null,
    backCursor: null,
    hasMoreData: false,
    limit: undefined,
    count: undefined,
  },
  
  fetchVendors: async () => {
    set({ isLoading: true });
    try {
      const res = await vendorService.get("/vendors");
      set({ vendors: res.data, isLoading: false });
    } catch (e: any) {
      set({ isLoading: false, error: e.message });
    }
  },

  createVendor: async (newVendorData: any) => {
    set({ isLoading: true });
    try {
      const res = await createVendor(newVendorData);
      const newVendor = res;

      set((state) => ({
        vendors: [newVendor, ...state.vendors],
        newVendors: [newVendor, ...state.newVendors],
        isLoading: false,
      }));
    } catch (e: any) {
      set({ isLoading: false, error: e.message });
    }
  },

  fetchData: async (options?: FetchOptions) => {
    const { startDate, endDate, actionType = "initial" } = options || {};
    if (actionType === "initial") {
      set({ isLoadingInitial: true });
    } else if (actionType === "filter") {
      set({ isLoadingFilter: true });
    }
    set({ isLoading: true });
    const { searchText, range, newVendors } = get();
    try {
      let query = `/vendors`;

      if (searchText.length > 0) {
        query += `/search?q=${searchText}`;
      } else {
        if (startDate && endDate) {
          query += `?startDate=${startDate}&endDate=${endDate}`;
        }
        if (searchText && searchText.length > 0)
          query += `search=${searchText}&`;
        if (range.length > 0) query += `range=${range.toLowerCase()}&`;
      }

      const res = await vendorService.get(query);
      let combinedVendors = res.data.data;
      if (searchText && searchText.length > 0) {
        const searchRegex = new RegExp(searchText, "i");
        const filteredNewVendors = newVendors.filter(
          (vendor) =>
            (vendor.name && searchRegex.test(vendor.name)) ||
            (vendor.lastName && searchRegex.test(vendor.lastName))
        );
        combinedVendors = [...filteredNewVendors, ...combinedVendors];
      }
      set({
        vendors: searchText.length > 0 ? res.data : combinedVendors,
        pagination: res.data.meta,
        isLoading: false,
        isLoadingInitial: false,
        isLoadingFilter: false,
      });
    } catch (e: any) {
      set({
        isLoading: false,
        error: e.message,
        isLoadingInitial: false,
        isLoadingFilter: false,
      });
    }
  },

  fetchMoreData: async () => {
    if (!get().pagination.hasMoreData || get().isLoadingMore) return;
    set({ isLoadingMore: true });
    const cursor = get().pagination.cursor;
    try {
      const res = await vendorService.get(`/vendors?cursor=${cursor}`);
      set((state) => ({
        vendors: [...state.vendors, ...res.data.data],
        pagination: res.data.meta,
        isLoadingMore: false,
      }));
    } catch (e: any) {
      set({ isLoadingMore: false, error: e.message });
    }
  },
  
  setSearchText: (text: string) => {
    set({ searchText: text });
  },
}));
